import React, { useMemo, useState } from 'react';
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameMonth, addMonths, subMonths,
  addWeeks, subWeeks, addDays, subDays, isToday,
} from 'date-fns';
import { es } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { TaskBoardTask } from '../../types';
import { PRIORITY_COLORS, ProjectPriority, CalendarMode } from '../../utils/projectConstants';
import { isTaskDone } from '../../utils/projectConstants';

interface ProjectCalendarViewProps {
  tasks: TaskBoardTask[];
  mode: CalendarMode;
  onModeChange: (mode: CalendarMode) => void;
  onTaskClick: (task: TaskBoardTask) => void;
  onCreateOnDate: (date: string) => void;
}

const ProjectCalendarView: React.FC<ProjectCalendarViewProps> = ({
  tasks,
  mode,
  onModeChange,
  onTaskClick,
  onCreateOnDate,
}) => {
  const [current, setCurrent] = useState(new Date());

  const tasksByDate = useMemo(() => {
    const map: Record<string, TaskBoardTask[]> = {};
    for (const task of tasks) {
      const date = task.due_date || task.start_date;
      if (!date) continue;
      if (!map[date]) map[date] = [];
      map[date].push(task);
    }
    return map;
  }, [tasks]);

  const navigate = (dir: -1 | 1) => {
    if (mode === 'month') setCurrent(dir === 1 ? addMonths(current, 1) : subMonths(current, 1));
    else if (mode === 'week') setCurrent(dir === 1 ? addWeeks(current, 1) : subWeeks(current, 1));
    else setCurrent(dir === 1 ? addDays(current, 1) : subDays(current, 1));
  };

  const headerLabel = mode === 'month'
    ? format(current, 'MMMM yyyy', { locale: es })
    : mode === 'week'
    ? `Semana del ${format(startOfWeek(current, { weekStartsOn: 1 }), 'd MMM', { locale: es })}`
    : format(current, 'EEEE d MMMM yyyy', { locale: es });

  const days = mode === 'month'
    ? eachDayOfInterval({
        start: startOfWeek(startOfMonth(current), { weekStartsOn: 1 }),
        end: endOfWeek(endOfMonth(current), { weekStartsOn: 1 }),
      })
    : mode === 'week'
    ? eachDayOfInterval({
        start: startOfWeek(current, { weekStartsOn: 1 }),
        end: endOfWeek(current, { weekStartsOn: 1 }),
      })
    : [current];

  const renderTaskPill = (task: TaskBoardTask) => {
    const priority = (task.priority || 'medium') as ProjectPriority;
    const done = isTaskDone(task.status, task.completed);
    return (
      <button
        key={task.id}
        type="button"
        onClick={() => onTaskClick(task)}
        className={`w-full text-left text-[10px] px-1.5 py-0.5 rounded truncate border-l-2 ${
          done ? 'opacity-50 line-through' : ''
        } bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700`}
        style={{ borderLeftColor: PRIORITY_COLORS[priority] }}
      >
        {task.title}
      </button>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => navigate(-1)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><ChevronLeft className="w-4 h-4" /></button>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white capitalize min-w-[180px] text-center">{headerLabel}</h2>
          <button type="button" onClick={() => navigate(1)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><ChevronRight className="w-4 h-4" /></button>
          <button type="button" onClick={() => setCurrent(new Date())} className="text-xs px-2 py-1 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 ml-2">Hoy</button>
        </div>
        <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {(['month', 'week', 'day'] as CalendarMode[]).map(m => (
            <button key={m} type="button" onClick={() => onModeChange(m)} className={`text-xs px-3 py-1.5 capitalize ${mode === m ? 'bg-indigo-600 text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}>
              {m === 'month' ? 'Mes' : m === 'week' ? 'Semana' : 'Día'}
            </button>
          ))}
        </div>
      </div>

      {mode === 'day' ? (
        <div className="flex-1 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/50 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className={`text-sm font-medium ${isToday(current) ? 'text-indigo-600' : 'text-gray-900 dark:text-white'}`}>
              {format(current, 'EEEE d MMMM', { locale: es })}
            </span>
            <button type="button" onClick={() => onCreateOnDate(format(current, 'yyyy-MM-dd'))} className="text-xs flex items-center gap-1 text-indigo-600 hover:text-indigo-700">
              <Plus className="w-3 h-3" /> Nueva tarea
            </button>
          </div>
          <div className="space-y-1">
            {(tasksByDate[format(current, 'yyyy-MM-dd')] || []).map(renderTaskPill)}
            {(tasksByDate[format(current, 'yyyy-MM-dd')] || []).length === 0 && (
              <p className="text-xs text-gray-400 py-8 text-center">Sin tareas para este día</p>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-7 mb-1">
            {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(d => (
              <div key={d} className="text-[10px] font-semibold uppercase text-gray-400 text-center py-1">{d}</div>
            ))}
          </div>
          <div className={`grid grid-cols-7 flex-1 ${mode === 'month' ? 'auto-rows-fr' : ''} gap-px bg-gray-200 dark:bg-gray-700 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700`}>
            {days.map(day => {
              const key = format(day, 'yyyy-MM-dd');
              const dayTasks = tasksByDate[key] || [];
              const inMonth = mode === 'week' || isSameMonth(day, current);
              return (
                <div
                  key={key}
                  className={`min-h-[80px] p-1.5 bg-white dark:bg-gray-900/80 ${!inMonth ? 'opacity-40' : ''}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[11px] font-medium w-5 h-5 flex items-center justify-center rounded-full ${
                      isToday(day) ? 'bg-indigo-600 text-white' : 'text-gray-600 dark:text-gray-400'
                    }`}>
                      {format(day, 'd')}
                    </span>
                    <button type="button" onClick={() => onCreateOnDate(key)} className="opacity-0 hover:opacity-100 p-0.5 text-gray-400 hover:text-indigo-600">
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="space-y-0.5">
                    {dayTasks.slice(0, mode === 'month' ? 3 : 8).map(renderTaskPill)}
                    {dayTasks.length > (mode === 'month' ? 3 : 8) && (
                      <span className="text-[9px] text-gray-400 pl-1">+{dayTasks.length - (mode === 'month' ? 3 : 8)} más</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectCalendarView;
